import { giphyDarkestGrey, giphyLightGrey, giphyWhite } from '@giphy/colors'
import { memo, useContext, useMemo } from 'react'
import CreationToolSourceEditor from 'site/creation-tools/types/source-editor'
import styled from 'styled-components'
import { BlackButton } from '../../buttons'
import { AllLayersIcon } from '../../icons'
import TrimmerComponent from '../../trimmer'
import { CreationSourceEditorContext } from '../manager'
import { SourceEditorCaption } from '../types'
import { Subtitle, TitleSmall } from './../../typography'
import Layer from './layer'

import { DndContext, MouseSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable'
import usePlayheadTime from '../hooks/use-playeadTime'

const Container = styled.div`
    grid-column: span 8;
`

const PreviewStripContainer = styled.div`
    position: relative;
`

const StyledSubtitle = styled(Subtitle)`
    color: ${giphyLightGrey};

    svg {
        margin-right: 0;
        height: 18px;
    }
`

const StyledInformativeTitle = styled(TitleSmall)`
    color: ${giphyWhite};
    margin-top: 5px;
`

const StyledInformativeText = styled(TitleSmall)`
    color: ${giphyLightGrey};
    font-weight: normal;
`

const LayersEditor = styled.div`
    margin-top: 34px;
`

const EmptyStateBox = styled.div`
    height: 224px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    background: ${giphyDarkestGrey};
    position: relative;
    border-radius: 6px;
`

const EmptyCryingImage = styled.img`
    position: absolute;
    top: -35px;
    width: 70px;
`

const ButtonsBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 30px;
`

const DraggableItemsContainer = styled.div`
    position: relative;
    overflow: auto;
    overflow-y: hidden;

    // This css above is to prevent drag the children to above the container
    will-change: transform;
    direction: ltr;
    margin-top: -25px;
    padding-top: 25px;
`

const TimelineHeader = () => {
    const { timeline, setTimeline, source } = useContext(CreationSourceEditorContext)
    const { currentPlayheadTime } = usePlayheadTime()

    return (
        <PreviewStripContainer>
            <TrimmerComponent
                {...{
                    source,
                    playheadTime: currentPlayheadTime,
                    defaultStartTime: timeline.start,
                    defaultEndTime: timeline.end,
                    onUpdate: (start, end) => setTimeline({ start, end }),
                }}
            />
        </PreviewStripContainer>
    )
}

type LayersListProps = {
    captions: SourceEditorCaption[]
}

const SortableLayersList = (props: LayersListProps) => {
    const { captions } = props
    const { setCaptions, setForceRedraw } = useContext(CreationSourceEditorContext)

    const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 5 } }))
    const items = captions.map((_, index) => `${index}`)

    const handleDragEnd = (event) => {
        const { active, over } = event

        if (active.id !== over.id) {
            const oldIndex = items.indexOf(active.id)
            const newIndex = items.indexOf(over.id)
            setForceRedraw(true)
            setCaptions(arrayMove(captions, oldIndex, newIndex))
        }
    }

    return (
        <DndContext
            sensors={sensors}
            modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <SortableContext items={items} strategy={verticalListSortingStrategy}>
                <DraggableItemsContainer>
                    {captions.map((caption, index) => (
                        <Layer key={index} caption={caption} index={index} />
                    ))}
                </DraggableItemsContainer>
            </SortableContext>
        </DndContext>
    )
}

const CreationSourceEditorLayersPanel = () => {
    const { captions, setCurrentEditor } = useContext(CreationSourceEditorContext)

    const filteredCaptions = useMemo(
        () => captions.filter((c) => c.text.length > 0),
        [captions]
    ) as SourceEditorCaption[]
    const sortableLayersListComponent = useMemo(
        () => <SortableLayersList captions={filteredCaptions} />,
        [filteredCaptions]
    )

    return (
        <Container>
            <TimelineHeader />
            <LayersEditor>
                <StyledSubtitle>
                    <AllLayersIcon /> All Layers
                </StyledSubtitle>
                {filteredCaptions.length > 0 ? (
                    sortableLayersListComponent
                ) : (
                    <EmptyStateBox>
                        <EmptyCryingImage
                            src="https://s3.amazonaws.com/giphygifs/media/ViHbdDMcIOeLeblrbq/200w.webp"
                            alt="emoji crying"
                        />
                        <StyledInformativeTitle>You don’t have any layers, yet!</StyledInformativeTitle>
                        <StyledInformativeText>
                            First add captions to your creation, then return here for layer tooling.
                        </StyledInformativeText>
                        <ButtonsBox>
                            <BlackButton onClick={() => setCurrentEditor(CreationToolSourceEditor.Caption)}>
                                Add Captions
                            </BlackButton>
                        </ButtonsBox>
                    </EmptyStateBox>
                )}
            </LayersEditor>
        </Container>
    )
}

export default memo(CreationSourceEditorLayersPanel)
